import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, apiRequest } from '../config/msalConfig';
import { setAccessTokenGetter, authAPI } from '../services/api';

// Build-time switch: only builds made with REACT_APP_AUTH_MODE=testing read a Testing-scheme token
// from sessionStorage. The API accepts such tokens only when it runs in the Testing environment.
const TEST_AUTH_MODE = process.env.REACT_APP_AUTH_MODE === 'testing';
export const TEST_TOKEN_STORAGE_KEY = 'vertolytics.testToken';

const readTestToken = () => (TEST_AUTH_MODE ? window.sessionStorage.getItem(TEST_TOKEN_STORAGE_KEY) : null);

// Mirrors RolePermissionMapping in src/Vertolytics.Core/Models/Permissions.cs; the API enforces it.
export const ROLE_PERMISSIONS = {
  super_admin: [
    'system:manage_all_accounts', 'system:view_logs', 'analytics:view_overview', 'analytics:view_call_log',
    'analytics:view_agent_analytics', 'analytics:view_queue_analytics', 'analytics:view_wallboard',
    'analytics:view_reports', 'analytics:view_insights', 'calls:view_content', 'reports:manage',
    'analytics:export_data', 'agents:view', 'agents:manage', 'queues:view', 'queues:manage', 'users:view',
    'users:manage', 'users:manage_roles', 'account:view_settings', 'account:manage_settings',
    'account:manage_billing', 'directory:manage',
  ],
  account_admin: [
    'analytics:view_overview', 'analytics:view_call_log', 'analytics:view_agent_analytics',
    'analytics:view_queue_analytics', 'analytics:view_wallboard', 'analytics:view_reports', 'analytics:view_insights',
    'calls:view_content', 'reports:manage', 'analytics:export_data', 'agents:view', 'agents:manage', 'queues:view',
    'queues:manage', 'users:view', 'users:manage', 'users:manage_roles', 'account:view_settings',
    'account:manage_settings', 'directory:manage',
  ],
  manager: [
    'analytics:view_overview', 'analytics:view_call_log', 'analytics:view_agent_analytics',
    'analytics:view_queue_analytics', 'analytics:view_wallboard', 'analytics:view_reports', 'analytics:view_insights',
    'calls:view_content', 'reports:manage', 'analytics:export_data', 'agents:view', 'agents:manage', 'queues:view',
    'users:view', 'account:view_settings', 'directory:manage',
  ],
  supervisor: [
    'analytics:view_overview', 'analytics:view_call_log', 'analytics:view_agent_analytics',
    'analytics:view_queue_analytics', 'analytics:view_wallboard', 'analytics:view_reports', 'analytics:view_insights',
    'calls:view_content', 'agents:view', 'queues:view', 'users:view', 'account:view_settings',
  ],
  agent: [
    'agents:view_own', 'account:view_settings',
  ],
  executive: [
    'analytics:view_overview', 'analytics:view_call_log', 'analytics:view_agent_analytics',
    'analytics:view_queue_analytics', 'analytics:view_wallboard', 'analytics:view_reports', 'analytics:view_insights',
    'reports:manage', 'analytics:export_data',
  ],
  board_member: [
    'analytics:view_overview', 'analytics:view_reports', 'analytics:view_insights',
  ],
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const msalAuthenticated = useIsAuthenticated();
  const [testToken, setTestToken] = useState(readTestToken);
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [resolveCount, setResolveCount] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const enrichmentId = useRef(0);

  const activeAccount = accounts[0];
  const signedIn = TEST_AUTH_MODE ? Boolean(testToken) : msalAuthenticated && Boolean(activeAccount);

  const getAccessToken = useCallback(async () => {
    if (TEST_AUTH_MODE) return testToken;
    if (!activeAccount) return null;

    try {
      const response = await instance.acquireTokenSilent({ ...apiRequest, account: activeAccount });
      return response.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect({ ...apiRequest, account: activeAccount });
        return null;
      }
      console.error('Token acquisition failed:', err.errorCode || err.message);
      return null;
    }
  }, [instance, activeAccount, testToken]);

  useEffect(() => {
    setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  useEffect(() => {
    if (!signedIn) {
      if (inProgress === InteractionStatus.None) {
        setUser(null);
        localStorage.removeItem('user');
        setResolveCount((c) => c + 1);
      }
      return;
    }

    const thisId = ++enrichmentId.current;
    (async () => {
      try {
        const backendUser = await authAPI.getCurrentUser();
        if (thisId !== enrichmentId.current) return;
        const enrichedUser = {
          id: backendUser.id,
          email: backendUser.email,
          name: backendUser.displayName || backendUser.email,
          accountId: backendUser.accountId,
          roles: backendUser.roles || [],
          needsOnboarding: backendUser.needsOnboarding === true,
          onboardingId: backendUser.onboardingId,
        };
        setError(null);
        setUser(enrichedUser);
        localStorage.setItem('user', JSON.stringify(enrichedUser));
      } catch (err) {
        if (thisId !== enrichmentId.current) return;
        const status = err.response?.status;
        console.error('Loading the Vertolytics profile failed:', status || err.message);
        setUser(null);
        localStorage.removeItem('user');
        setError(
          status === 403
            ? 'Your Vertolytics access is disabled. Contact your administrator.'
            : 'We could not load your Vertolytics profile. Try again, or sign out and use a different account.'
        );
      } finally {
        if (thisId === enrichmentId.current) {
          setResolveCount((c) => c + 1);
        }
      }
    })();
  }, [signedIn, activeAccount, inProgress, retryCount]);

  const login = useCallback(async () => {
    setError(null);
    if (TEST_AUTH_MODE) {
      const token = readTestToken();
      if (!token) setError(`Testing auth mode: set sessionStorage["${TEST_TOKEN_STORAGE_KEY}"] to a Testing token.`);
      setTestToken(token);
      return { success: Boolean(token) };
    }

    try {
      await instance.loginRedirect(loginRequest);
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  }, [instance]);

  const retryProfile = useCallback(() => {
    setError(null);
    setRetryCount((c) => c + 1);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('user');
    if (TEST_AUTH_MODE) {
      window.sessionStorage.removeItem(TEST_TOKEN_STORAGE_KEY);
      setTestToken(null);
      setUser(null);
      return;
    }
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  }, [instance]);

  const hasPermission = useCallback(
    (permission) => Boolean(user?.roles?.some((role) => (ROLE_PERMISSIONS[role] || []).includes(permission))),
    [user]
  );

  const hasRole = useCallback((role) => Boolean(user?.roles?.includes(role)), [user]);

  const isLoading = resolveCount === 0 || inProgress !== InteractionStatus.None;

  const value = {
    user,
    loading: isLoading,
    error,
    login,
    logout,
    retryProfile,
    hasPermission,
    hasRole,
    isAuthenticated: signedIn && user !== null,
    signedIn,
    getAccessToken,
    setUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
